/**
 * @file ShipmentProblemNotes.tsx
 * @description Пометки проблемной отгрузки в карточке (phase19.13 шаг 6).
 *
 * Два элемента одной темы: бейдж «зависла» в ряду статусов и строка-объяснение
 * под ним. Текст пишем действием менеджера («свяжитесь с клиентом»), а не
 * состоянием системы — карточку читают в момент разбора инцидента.
 */

import { TriangleAlert } from "lucide-react";

import { Badge } from "../ui/badge";
import type { DeliveryStage } from "../../lib/api-delivery-monitoring";
import type { ShipmentStalenessBadge } from "../../lib/shipment-state";

/** Что делать менеджеру при плохом финале доставки. */
const PROBLEM_TEXT: Partial<Record<DeliveryStage, string>> = {
  NOT_DELIVERED: "Вручение не удалось. Свяжитесь с клиентом.",
  RETURNED: "Отправление возвращено. Примите товар и решите вопрос с деньгами.",
};

/** Бейдж «зависла» — в ряд со статусом и перевозчиком. */
export function ShipmentStaleBadge({ badge }: { badge: ShipmentStalenessBadge }) {
  return (
    <Badge variant={badge.tone === "destructive" ? "destructive" : "warning"}>
      <TriangleAlert />
      {badge.label}
    </Badge>
  );
}

interface NoteProps {
  /** Этап последнего события ленты. */
  stage: DeliveryStage | null;
  /** Комментарий перевозчика к этому событию (причина недоставки). */
  comment: string | null;
}

/**
 * Строка-объяснение под карточкой. Показывается только на «не вручено» и
 * «возврат» — остальные этапы менеджеру и так понятны из истории.
 */
export function ShipmentProblemNote({ stage, comment }: NoteProps) {
  if (!stage) return null;
  const text = PROBLEM_TEXT[stage];
  if (!text) return null;

  // Причину недоставки цитируем дословно: спорить с перевозчиком придётся ей.
  const withReason =
    stage === "NOT_DELIVERED" && comment
      ? `Вручение не удалось: «${comment}». Свяжитесь с клиентом.`
      : text;

  return (
    <p className="mt-2 flex w-full items-start gap-1.5 rounded-md bg-destructive/10 px-2 py-1.5 text-sm">
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
      <span className="min-w-0 break-words">{withReason}</span>
    </p>
  );
}
